import * as React from "react";
import { useGlobal } from "kanopi-components/useGlobal";

import { CoverSectionsGrid } from "./CoverSectionsGrid";
import { ActiveCoverSection as StatelessActiveCoverSection } from "./ActiveCoverSection";
import { useUpdateCoverEnabled } from "./useUpdateCoverEnabled";
import { useEditCoverSection } from "./useEditCoverSection";
import { AddCoverTile } from "../../quoting/components/AddCoverTile";
import { CoverSectionModal } from "./CoverSectionModal";
import { useGlobalsContext } from "../Globals";

type TActiveCoverSectionProps = {
  coverSections: TCoverSection[];
  prefix: string;
  riskProfile?: TRiskProfile;
  inactiveSectionRef?: React.RefObject<HTMLDivElement>;
  allowAdd?: boolean;
  finishedRoute?: string;
  policyId?: string;
  onUpdatingCoverSection?: (coverSection: TCoverSection) => void;
};

export const ActiveCoverSections: React.FC<TActiveCoverSectionProps> = ({
  coverSections = [],
  prefix,
  riskProfile,
  inactiveSectionRef,
  allowAdd,
  finishedRoute,
  policyId,
  ...props
}) => {
  const [updatingCoverSection, setUpdatingCoverSection] =
    React.useState<TCoverSection>();
  const { startEditing, coverId } = useEditCoverSection({
    coverSection: updatingCoverSection,
    prefix,
  });
  const parentCoverSections = coverSections
    .filter(({ parentCover }) => !parentCover)
    .map((c) => ({
      ...c,
      childCovers: coverSections.filter(({ parentCover }) => parentCover?.id === c.id),
    }));
  const makeHandleEdit = (cover: TCoverSection) => () => {
    setUpdatingCoverSection(cover);
  };
  React.useEffect(() => {
    props.onUpdatingCoverSection?.(updatingCoverSection);

    if (updatingCoverSection) {
      startEditing();
    }
  }, [updatingCoverSection]);
  React.useEffect(() => {
    if (coverId && !updatingCoverSection) {
      setUpdatingCoverSection(coverSections.find(({ id }) => id === coverId));
    }
  }, [coverId]);

  return (
    <CoverSectionsGrid columns={4} data-testid="activeCoverSections">
      {parentCoverSections.map((coverSection) => (
        <ActiveCoverSection
          key={coverSection.id}
          coverSection={coverSection}
          riskProfile={riskProfile}
          removeEnabled={parentCoverSections.length > 1}
          edit={makeHandleEdit(coverSection)}
          finishedRoute={finishedRoute}
          {...props}
          editable={coverSection.editable}
        >
          {coverSection.childCovers.map((c) => (
            <ActiveCoverSection
              key={c.id}
              coverSection={c}
              riskProfile={riskProfile}
              edit={makeHandleEdit(c)}
              finishedRoute={finishedRoute}
              removeEnabled
              {...props}
            />
          ))}
        </ActiveCoverSection>
      ))}
      {allowAdd && <AddCoverTile inactiveSectionRef={inactiveSectionRef} />}
      <CoverSectionModal
        coverSection={updatingCoverSection}
        riskProfileId={riskProfile.id}
        prefix={prefix}
        title={updatingCoverSection?.title}
        description={updatingCoverSection?.description}
        coverIsEnabled
        onClose={() => setUpdatingCoverSection(undefined)}
        policyId={policyId}
      />
    </CoverSectionsGrid>
  );
};

type TProps = {
  coverSection?: TCoverSection;
  disabled?: boolean;
  editable?: boolean;
  removeEnabled?: boolean;
  prefix?: string;
  finishedRoute?: string;
  riskProfile: TRiskProfile;
  children?: React.ReactNode;
  edit?: (...args: unknown[]) => void;
  key?: string;
};

export function ActiveCoverSection({
  coverSection = {},
  disabled,
  editable = true,
  removeEnabled = false,
  riskProfile,
  edit,
  children,
  finishedRoute,
}: TProps) {
  const globalsContext = useGlobalsContext();
  const [data, setData] = useGlobal(globalsContext);
  const { setEnabled } = useUpdateCoverEnabled({ coverId: coverSection.id });

  async function disableCoverSection() {
    await setEnabled(false);
    setData({ reprice: true });
  }

  return (
    <StatelessActiveCoverSection
      coverSection={coverSection}
      riskProfile={riskProfile}
      disable={editable && removeEnabled ? disableCoverSection : null}
      edit={editable ? edit : null}
      disabled={disabled || data?.reprice}
      finishedRoute={finishedRoute}
    >
      {children}
    </StatelessActiveCoverSection>
  );
}
